import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Typography,
  Switch,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import PhoneInput from "../../common/phone-input";
import AppDatePicker from "../../common/app-date-picker";
import { ColorsConstants } from "../../cssUtils/constants";

const FREQUENCY_OPTIONS = [
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Half-Yearly", value: "half_yearly" },
  { label: "Yearly", value: "yearly" },
];

const EMPTY_ERRORS = {
  customer_name: "",
  phone_no: "",
  fund_name: "",
  sip_amount: "",
  sip_frequency: "",
  last_sip_payment_date: "",
};

const EMPTY_FORM = {
  customer_name: "",
  country_code: "+91",
  phone_no: "",
  fund_name: "",
  sip_amount: "",
  sip_frequency: "",
  last_sip_payment_date: null,
  active: true,
};

const SipRecordFormDialog = ({
  open,
  handleClose,
  onSave,
  editData,
  saving = false,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState(EMPTY_ERRORS);

  useEffect(() => {
    if (editData) {
      setForm({
        customer_name: editData.customer_name || "",
        country_code: editData.country_code
          ? `+${editData.country_code}`
          : "+91",
        phone_no: editData.phone_number || "",
        fund_name: editData.fund_name || "",
        sip_amount: editData.sip_amount ?? "",
        sip_frequency: editData.sip_frequency || "",
        last_sip_payment_date: editData.last_sip_payment_date
          ? dayjs(editData.last_sip_payment_date)
          : null,
        active: editData.active ?? true,
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors(EMPTY_ERRORS);
  }, [editData, open]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field in EMPTY_ERRORS) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validatePhone = (phone) => {
    const value = (phone || "").trim();
    if (!value) return "Phone Number is required";
    if (!/^\d+$/.test(value)) return "Phone number must contain digits only";
    if (value.length < 6 || value.length > 15)
      return "Please enter a valid phone number";
    return "";
  };

  const validate = () => {
    const next = { ...EMPTY_ERRORS };
    if (!form.customer_name.trim())
      next.customer_name = "Customer Name is required";
    if (!form.fund_name.trim()) next.fund_name = "Fund Name is required";
    if (!form.sip_amount || Number(form.sip_amount) <= 0)
      next.sip_amount = "Enter a valid SIP amount";
    if (!form.sip_frequency) next.sip_frequency = "SIP Frequency is required";
    if (!form.last_sip_payment_date)
      next.last_sip_payment_date = "Last SIP Payment Date is required";
    next.phone_no = validatePhone(form.phone_no);
    setErrors(next);
    return Object.values(next).every((e) => !e);
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({
      customer_name: form.customer_name.trim(),
      country_code: form.country_code.replace("+", ""),
      phone_number: form.phone_no.trim(),
      fund_name: form.fund_name.trim(),
      sip_amount: form.sip_amount,
      sip_frequency: form.sip_frequency,
      last_sip_payment_date: form.last_sip_payment_date
        ? dayjs(form.last_sip_payment_date).format("YYYY-MM-DD")
        : null,
      active: form.active,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {editData ? "Edit SIP Record" : "Add SIP Record"}
        <IconButton onClick={handleClose} disabled={saving}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Customer Name"
            value={form.customer_name}
            onChange={(e) => set("customer_name", e.target.value)}
            fullWidth
            required
            error={!!errors.customer_name}
            helperText={errors.customer_name}
          />

          <PhoneInput
            countryCode={form.country_code}
            phoneNo={form.phone_no}
            onCountryCodeChange={(v) => set("country_code", v)}
            onPhoneNoChange={(v) => {
              set("phone_no", v);
              setErrors((prev) => ({ ...prev, phone_no: validatePhone(v) }));
            }}
            error={!!errors.phone_no}
            helperText={errors.phone_no}
            fullWidth
          />

          <TextField
            label="Fund Name"
            value={form.fund_name}
            onChange={(e) => set("fund_name", e.target.value)}
            fullWidth
            required
            error={!!errors.fund_name}
            helperText={errors.fund_name}
          />

          <Box display="flex" gap={2}>
            <TextField
              label="SIP Amount"
              type="number"
              value={form.sip_amount}
              onChange={(e) => set("sip_amount", e.target.value)}
              fullWidth
              required
              error={!!errors.sip_amount}
              helperText={errors.sip_amount}
            />
            <TextField
              select
              label="SIP Frequency"
              value={form.sip_frequency}
              onChange={(e) => set("sip_frequency", e.target.value)}
              fullWidth
              required
              error={!!errors.sip_frequency}
              helperText={errors.sip_frequency}
            >
              {FREQUENCY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <AppDatePicker
              label="Last SIP Payment Date"
              value={form.last_sip_payment_date}
              onChange={(v) => set("last_sip_payment_date", v)}
              disableFuture
              error={!!errors.last_sip_payment_date}
              helperText={errors.last_sip_payment_date}
            />
          </LocalizationProvider>

          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="body2">Active</Typography>
            <Switch
              checked={form.active}
              onChange={(e) => set("active", e.target.checked)}
              size="small"
              sx={{
                width: 36,
                height: 20,
                padding: 0,
                "& .MuiSwitch-switchBase": {
                  padding: 0,
                  margin: "2px",
                  "&.Mui-checked": {
                    transform: "translateX(16px)",
                    color: "#fff",
                    "& + .MuiSwitch-track": {
                      backgroundColor: ColorsConstants.primaryBackgroundColor,
                      opacity: 1,
                    },
                  },
                },
                "& .MuiSwitch-thumb": {
                  width: 16,
                  height: 16,
                  boxShadow: "none",
                },
                "& .MuiSwitch-track": {
                  borderRadius: 10,
                  backgroundColor: "#C8D0E0",
                  opacity: 1,
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3 }}>
        <ButtonTwo onClick={handleClose} disabled={saving}>
          Cancel
        </ButtonTwo>
        <ButtonOne onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : editData ? "Update" : "Add"}
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

export default SipRecordFormDialog;
